/** Original procedural environments. No textures, network requests, or game IDs. */
export const environmentShader = /* wgsl */ `
struct Environment {
  inverseViewProjection: mat4x4<f32>,
  eyeTime: vec4<f32>,
  viewport: vec4<f32>, // physical width, height, preset, reserved
  center: vec4<f32>, // table center xyz, water plane height
  tableSize: vec4<f32>,
  viewProjection: mat4x4<f32>,
  lightViewProjection: mat4x4<f32>,
};
@group(0) @binding(0) var<uniform> environment: Environment;
@group(0) @binding(1) var shadowMap: texture_depth_2d;
@group(0) @binding(2) var shadowSampler: sampler_comparison;

@vertex fn environmentVertex(@builtin(vertex_index) index: u32) -> @builtin(position) vec4<f32> {
  let x = f32((index << 1u) & 2u);
  let y = f32(index & 2u);
  return vec4<f32>(x * 2.0 - 1.0, y * 2.0 - 1.0, 0.999, 1.0);
}

fn hash21(point: vec2<f32>) -> f32 {
  var p = fract(vec3<f32>(point.xyx) * 0.1031);
  p += dot(p, p.yzx + vec3<f32>(33.33));
  return fract((p.x + p.y) * p.z);
}

fn noise(point: vec2<f32>) -> f32 {
  let cell = floor(point);
  let local = fract(point);
  let blend = local * local * (vec2<f32>(3.0) - 2.0 * local);
  return mix(mix(hash21(cell), hash21(cell + vec2<f32>(1.0, 0.0)), blend.x),
    mix(hash21(cell + vec2<f32>(0.0, 1.0)), hash21(cell + vec2<f32>(1.0)), blend.x), blend.y);
}

fn cloud(point: vec2<f32>) -> f32 {
  var p = point;
  var result = noise(p) * 0.57;
  p = vec2<f32>(p.x * 1.62 + p.y * 1.15, p.y * 1.62 - p.x * 1.15) + vec2<f32>(8.1, 19.6);
  result += noise(p) * 0.28;
  p = vec2<f32>(p.x * 1.62 + p.y * 1.15, p.y * 1.62 - p.x * 1.15) + vec2<f32>(8.1, 19.6);
  return result + noise(p) * 0.15;
}

fn roundedTableDistance(point: vec2<f32>) -> f32 {
  let radius = 0.65;
  let q = abs(point) - environment.tableSize.xy * 0.5 + vec2<f32>(radius);
  return length(max(q, vec2<f32>(0.0))) + min(max(q.x, q.y), 0.0) - radius;
}

fn surfaceShadow(world: vec3<f32>) -> f32 {
  let lightPosition = environment.lightViewProjection * vec4<f32>(world, 1.0);
  let projected = lightPosition.xyz / lightPosition.w;
  let uv = projected.xy * vec2<f32>(0.5, -0.5) + vec2<f32>(0.5);
  if (any(uv < vec2<f32>(0.0)) || any(uv > vec2<f32>(1.0)) || projected.z < 0.0 || projected.z > 1.0) { return 1.0; }
  let texel = 1.5 / vec2<f32>(textureDimensions(shadowMap));
  var visibility = 0.0;
  for (var y = -1; y <= 1; y += 1) {
    for (var x = -1; x <= 1; x += 1) {
      visibility += textureSampleCompareLevel(shadowMap, shadowSampler, uv + vec2<f32>(f32(x), f32(y)) * texel, projected.z - 0.0004);
    }
  }
  return visibility / 9.0;
}

fn ocean(ray: vec3<f32>) -> vec3<f32> {
  let sky = mix(vec3<f32>(0.26, 0.48, 0.54), vec3<f32>(0.64, 0.77, 0.77), pow(1.0 - abs(ray.y), 3.0));
  if (ray.y >= -0.002) { return sky; }
  let distance = (environment.center.w - environment.eyeTime.y) / ray.y;
  if (distance < 0.0) { return sky; }
  let world = environment.eyeTime.xyz + ray * distance;
  let p = world.xz - environment.center.xz;
  let time = environment.eyeTime.w;
  let shore = roundedTableDistance(p);
  let seabed = cloud(p * 0.19 + vec2<f32>(9.0, 3.0));
  let shallows = exp(-max(shore, 0.0) * 0.42) * (0.78 + seabed * 0.22);
  let depthVariation = cloud(p * 0.09 + vec2<f32>(13.0, 41.0));
  let deepWater = mix(vec3<f32>(0.065, 0.31, 0.36), vec3<f32>(0.075, 0.41, 0.44), depthVariation);
  var water = mix(deepWater, vec3<f32>(0.17, 0.61, 0.59), shallows * 0.86);

  // Broad waves travel a fraction of a component width per second. Their
  // analytic normal creates silky bands, with no sharp specular sparkle.
  let a = dot(p, vec2<f32>(0.72, 1.62)) + time * 0.19;
  let b = dot(p, vec2<f32>(-1.35, 0.58)) - time * 0.13 + sin(a * 0.31) * 0.7;
  let c = dot(p, vec2<f32>(0.34, 3.10)) + time * 0.24 + sin(b * 0.41) * 0.5;
  let d = dot(p, vec2<f32>(2.35, 0.71)) - time * 0.16;
  let dx = cos(a) * 0.031 + cos(b) * -0.024 + cos(c) * 0.007 + cos(d) * 0.010;
  let dz = cos(a) * 0.063 + cos(b) * 0.010 + cos(c) * 0.061 + cos(d) * 0.007;
  let normal = normalize(vec3<f32>(-dx, 1.0, -dz));
  let view = -ray;
  let light = normalize(vec3<f32>(-0.65, 1.3, 0.65));
  let halfVector = normalize(light + view);
  let reflection = pow(max(dot(normal, halfVector), 0.0), 8.0);
  let waveLight = (sin(a) * 0.011 + sin(b) * 0.008 + sin(c) * 0.006);
  let visibleDetail = 1.0 - smoothstep(45.0, 110.0, distance);
  water += vec3<f32>(0.74, 0.88, 0.83) * (waveLight + reflection * 0.043) * visibleDetail;

  // Broken, soft wave crests avoid a contour-map pattern. They fade before
  // their world-space frequency becomes smaller than distant screen pixels.
  let warp = noise(p * 0.45 + vec2<f32>(time * 0.009, -time * 0.007));
  let crest = sin(p.y * 5.2 + p.x * 1.3 + warp * 5.8 - time * 0.16);
  let breakup = smoothstep(0.48, 0.76, noise(p * vec2<f32>(1.55, 0.95)));
  let ripples = smoothstep(0.72, 0.99, crest) * breakup;
  water += vec3<f32>(0.64, 0.82, 0.77) * ripples * 0.038 * visibleDetail;
  let tide = sin(p.x * 0.7 + p.y * 0.58 + time * 0.16) * 0.07;
  let shorelineFoam = exp(-pow((shore - 0.08 - tide) / 0.16, 2.0)) * (0.24 + 0.40 * noise(p * 3.4));
  water = mix(water, vec3<f32>(0.71, 0.85, 0.77), shorelineFoam * 0.35);
  let fresnel = pow(1.0 - max(dot(normal, view), 0.0), 4.0);
  water = mix(water, sky, fresnel * 0.45);
  water *= mix(0.68, 1.0, surfaceShadow(world));
  return mix(water, sky, smoothstep(100.0, 240.0, distance));
}

fn starLayer(uv: vec2<f32>, scale: f32, seed: f32, pixelSize: f32) -> vec3<f32> {
  let grid = uv * scale;
  let cell = floor(grid);
  let value = hash21(cell + vec2<f32>(seed, seed * 3.7));
  let offset = vec2<f32>(hash21(cell + vec2<f32>(13.1 + seed, 5.4)), hash21(cell + vec2<f32>(7.7, seed + 31.0))) * 0.6 + 0.2;
  let d = length(fract(grid) - offset);
  let radius = pixelSize * scale * mix(0.48, 1.15, pow(value, 16.0));
  let core = (1.0 - smoothstep(radius * 0.25, radius * 1.55, d)) * step(0.78, value);
  let glow = exp(-d * d / max(radius * radius * 7.0, 0.00001)) * step(0.985, value) * 0.16;
  let color = mix(vec3<f32>(0.53, 0.68, 0.91), vec3<f32>(0.96, 0.84, 0.64), hash21(cell + vec2<f32>(12.9, 31.7)));
  return color * (core * mix(0.20, 0.76, value) + glow);
}

fn space(ray: vec3<f32>) -> vec3<f32> {
  // A distant celestial sphere follows camera orientation rather than screen
  // coordinates. Twenty minutes of idle time shifts it less than one degree.
  let drift = environment.eyeTime.w * 0.000007;
  let direction = vec3<f32>(ray.x * cos(drift) - ray.z * sin(drift), ray.y, ray.x * sin(drift) + ray.z * cos(drift));
  let uv = vec2<f32>(atan2(direction.z, direction.x) / 6.2831853 + 0.5, asin(clamp(direction.y, -1.0, 1.0)) / 3.14159265 + 0.5);
  let haze = cloud(direction.xy * 3.4 + vec2<f32>(direction.z * 2.2, 13.0));
  let ribbon = exp(-pow((direction.x * 0.56 + direction.y * 0.18 - direction.z * 0.42 - 0.21) * 3.7, 2.0));
  let dust = cloud(direction.yz * 9.0 + vec2<f32>(31.0, direction.x * 5.0));
  var color = vec3<f32>(0.018, 0.029, 0.071);
  color += vec3<f32>(0.030, 0.042, 0.105) * haze;
  color += mix(vec3<f32>(0.028, 0.065, 0.086), vec3<f32>(0.086, 0.031, 0.093), haze) * ribbon * dust * 0.7;
  let pixelSize = 0.205 / max(environment.viewport.y, 1.0);
  let stars = starLayer(uv, 155.0, 1.0, pixelSize) + starLayer(uv, 290.0, 15.0, pixelSize * 0.72) * 0.55;
  color += stars;

  // A small, shaded crescent sits beyond the table. Its limb and atmosphere are
  // analytic, so zooming and orbiting remain smooth without a billboard asset.
  let axis = normalize(vec3<f32>(-0.40, -0.66, -0.65));
  let tangent = normalize(cross(vec3<f32>(0.0, 1.0, 0.0), axis));
  let up = cross(axis, tangent);
  let local = vec2<f32>(dot(direction, tangent), dot(direction, up)) / 0.115;
  let r2 = dot(local, local);
  let front = step(0.0, dot(direction, axis));
  let atmosphere = exp(-abs(sqrt(r2) - 1.0) * 30.0) * front;
  color += vec3<f32>(0.15, 0.30, 0.44) * atmosphere * 0.3;
  if (r2 < 1.0 && front > 0.0) {
    let normal = vec3<f32>(local, sqrt(1.0 - r2));
    let light = normalize(vec3<f32>(-0.90, 0.42, -0.15));
    let lit = max(dot(normal, light), 0.0);
    let bands = cloud(local * vec2<f32>(4.8, 11.0) + vec2<f32>(23.0, 5.0));
    let land = mix(vec3<f32>(0.11, 0.19, 0.29), vec3<f32>(0.40, 0.54, 0.62), bands);
    let planet = land * (0.10 + lit * 0.80) + vec3<f32>(0.006, 0.011, 0.025);
    color = mix(color, planet, 1.0 - smoothstep(0.987, 1.0, sqrt(r2)));
  }
  return color;
}

fn studio(ray: vec3<f32>) -> vec3<f32> {
  let sky = mix(vec3<f32>(0.115, 0.128, 0.131), vec3<f32>(0.215, 0.226, 0.221), clamp(-ray.y * 0.6 + 0.3, 0.0, 1.0));
  if (ray.y >= -0.002) { return sky; }
  let distance = (environment.center.w - environment.eyeTime.y) / ray.y;
  if (distance < 0.0) { return sky; }
  let world = environment.eyeTime.xyz + ray * distance;
  let p = world.xz - environment.center.xz;
  let radius = length(p / max(environment.tableSize.xy, vec2<f32>(1.0)));
  let pool = exp(-radius * radius * 0.56);
  var floorColor = mix(vec3<f32>(0.115, 0.132, 0.134), vec3<f32>(0.24, 0.253, 0.24), pool);
  let shadow = exp(-max(roundedTableDistance(p), 0.0) * 1.6) * 0.12;
  floorColor *= 1.0 - shadow;
  floorColor *= mix(0.82, 1.0, surfaceShadow(world));
  return mix(floorColor, sky, smoothstep(90.0, 220.0, distance));
}

struct EnvironmentFragment {
  @location(0) color: vec4<f32>,
  @builtin(frag_depth) depth: f32,
};

@fragment fn environmentFragment(@builtin(position) position: vec4<f32>) -> EnvironmentFragment {
  let screen = position.xy / environment.viewport.xy;
  let ndc = vec2<f32>(screen.x * 2.0 - 1.0, 1.0 - screen.y * 2.0);
  let far = environment.inverseViewProjection * vec4<f32>(ndc, 1.0, 1.0);
  let ray = normalize(far.xyz / far.w - environment.eyeTime.xyz);
  var color: vec3<f32>;
  var output: EnvironmentFragment;
  output.depth = 1.0;
  if (environment.viewport.z > 1.5) {
    color = space(ray);
  } else if (environment.viewport.z > 0.5) {
    color = ocean(ray);
  } else {
    color = studio(ray);
  }
  // Water and the studio floor participate in the actual scene depth buffer.
  // Submerged hulls and shoreline rocks disappear beneath this physical plane;
  // distant space remains at the far depth, independent of camera position.
  if (environment.viewport.z < 1.5 && ray.y < -0.002) {
    let distance = (environment.center.w - environment.eyeTime.y) / ray.y;
    if (distance > 0.0) {
      let world = environment.eyeTime.xyz + ray * distance;
      let projected = environment.viewProjection * vec4<f32>(world, 1.0);
      output.depth = clamp(projected.z / projected.w, 0.0, 1.0);
    }
  }
  // The restrained vignette frames play without attaching animated detail to
  // the UI. Procedural colors are already display-referred, as is canvas output.
  let vignette = 1.0 - smoothstep(0.28, 0.78, length(screen - vec2<f32>(0.5))) * 0.09;
  output.color = vec4<f32>(clamp(color * vignette, vec3<f32>(0.0), vec3<f32>(1.0)), 1.0);
  return output;
}
`
